import React, { useMemo, useState } from "react";
import {
  Wrench,
  Plus,
  Search,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import "../../styles/AdditionalServices.css";

const initialServices = [
  {
    id: 1,
    name: "Unpacking Service",
    category: "Packing Services",
    description: "Unpacking and arranging goods at the destination",
    pricingType: "Fixed",
    price: 2500,
    status: "Active",
  },
  {
    id: 2,
    name: "Furniture Dismantling",
    category: "Furniture Services",
    description: "Dismantling furniture before transportation",
    pricingType: "Fixed",
    price: 1500,
    status: "Active",
  },
  {
    id: 3,
    name: "Furniture Assembly",
    category: "Furniture Services",
    description: "Assembly of furniture after delivery",
    pricingType: "Fixed",
    price: 1800,
    status: "Active",
  },
  {
    id: 4,
    name: "Storage Service",
    category: "Storage",
    description: "Temporary storage of household goods",
    pricingType: "Per Day",
    price: 500,
    status: "Active",
  },
  {
    id: 5,
    name: "Deep Cleaning",
    category: "Cleaning Services",
    description: "Deep cleaning of property before or after moving",
    pricingType: "Fixed",
    price: 3500,
    status: "Active",
  },
  {
    id: 6,
    name: "Vehicle Loading",
    category: "Vehicle Transportation",
    description: "Special loading support for vehicle transportation",
    pricingType: "Fixed",
    price: 1200,
    status: "Active",
  },
  {
    id: 7,
    name: "Packing Materials",
    category: "Packing Services",
    description: "Additional cartons, bubble wrap and packing materials",
    pricingType: "Variable",
    price: 1000,
    status: "Active",
  },
  {
    id: 8,
    name: "Transit Insurance",
    category: "Additional Services",
    description: "Optional insurance coverage during transportation",
    pricingType: "Percentage",
    price: 2,
    status: "Inactive",
  },
];

export default function AdditionalServices() {
  const [services, setServices] = useState(initialServices);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 6;

  const filteredServices = useMemo(() => {
    return services.filter((item) => {
      const searchValue = search.toLowerCase();

      const matchesSearch =
        item.name.toLowerCase().includes(searchValue) ||
        item.description.toLowerCase().includes(searchValue) ||
        item.category.toLowerCase().includes(searchValue);

      const matchesCategory =
        !categoryFilter ||
        item.category === categoryFilter;

      const matchesStatus =
        !statusFilter ||
        item.status === statusFilter;

      return (
        matchesSearch &&
        matchesCategory &&
        matchesStatus
      );
    });
  }, [services, search, categoryFilter, statusFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredServices.length / rowsPerPage)
  );

  const currentPage = Math.min(page, totalPages);

  const currentRows = filteredServices.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const activeCount = services.filter(
    (item) => item.status === "Active"
  ).length;

  const inactiveCount = services.filter(
    (item) => item.status === "Inactive"
  ).length;

  const categoriesCount = new Set(
    services.map((item) => item.category)
  ).size;

  const clearFilters = () => {
    setSearch("");
    setCategoryFilter("");
    setStatusFilter("");
    setPage(1);
  };

  const toggleStatus = (id) => {
    setServices((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status:
                item.status === "Active"
                  ? "Inactive"
                  : "Active",
            }
          : item
      )
    );
  };

  const deleteService = (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this additional service?"
    );

    if (!confirmed) return;

    setServices((prev) =>
      prev.filter((item) => item.id !== id)
    );
  };

  const handleAdd = () => {
    alert(
      "Add Additional Service form will be connected here."
    );
  };

  const handleEdit = (item) => {
    alert(`Edit Additional Service: ${item.name}`);
  };

  const formatPrice = (item) => {
    if (item.pricingType === "Percentage") {
      return `${item.price}%`;
    }

    return `₹${item.price.toLocaleString("en-IN")}`;
  };

  return (
    <div className="additional-services-page">

      {/* Header */}
      <div className="additional-services-header">

        <div className="additional-services-title">

          <div className="additional-services-icon">
            <Wrench size={22} />
          </div>

          <div>
            <h1>Additional Services</h1>
            <p>
              Manage optional services available during quotation
            </p>
          </div>

        </div>

        <button
          className="additional-service-add-btn"
          onClick={handleAdd}
        >
          <Plus size={18} />
          Add Service
        </button>

      </div>

      {/* Summary */}
      <div className="additional-service-summary-grid">

        <div className="additional-service-summary-card">

          <div className="additional-service-summary-icon total">
            <Wrench size={20} />
          </div>

          <div>
            <span>Total Services</span>
            <strong>{services.length}</strong>
          </div>

        </div>

        <div className="additional-service-summary-card">

          <div className="additional-service-summary-icon active">
            <CheckCircle2 size={20} />
          </div>

          <div>
            <span>Active Services</span>
            <strong>{activeCount}</strong>
          </div>

        </div>

        <div className="additional-service-summary-card">

          <div className="additional-service-summary-icon category">
            <Wrench size={20} />
          </div>

          <div>
            <span>Categories</span>
            <strong>{categoriesCount}</strong>
          </div>

        </div>

        <div className="additional-service-summary-card">

          <div className="additional-service-summary-icon inactive">
            <XCircle size={20} />
          </div>

          <div>
            <span>Inactive Services</span>
            <strong>{inactiveCount}</strong>
          </div>

        </div>

      </div>

      {/* Filters */}
      <div className="additional-service-filter-card">

        <div className="additional-service-search">

          <Search size={17} />

          <input
            type="text"
            placeholder="Search service..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

        </div>

        <select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Categories</option>
          <option value="Packing Services">
            Packing Services
          </option>
          <option value="Furniture Services">
            Furniture Services
          </option>
          <option value="Storage">
            Storage
          </option>
          <option value="Cleaning Services">
            Cleaning Services
          </option>
          <option value="Vehicle Transportation">
            Vehicle Transportation
          </option>
          <option value="Additional Services">
            Additional Services
          </option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>

        {(search ||
          categoryFilter ||
          statusFilter) && (
          <button
            className="additional-service-clear-btn"
            onClick={clearFilters}
          >
            <X size={15} />
            Clear
          </button>
        )}

      </div>

      {/* Desktop Table */}
      <div className="additional-service-table-card">

        <div className="additional-service-table-wrapper">

          <table className="additional-service-table">

            <thead>
              <tr>
                <th>#</th>
                <th>Service</th>
                <th>Category</th>
                <th>Description</th>
                <th>Pricing</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>

              {currentRows.length > 0 ? (
                currentRows.map((item, index) => (
                  <tr key={item.id}>

                    <td>
                      {(currentPage - 1) * rowsPerPage +
                        index +
                        1}
                    </td>

                    <td>
                      <div className="additional-service-name">

                        <div className="additional-service-row-icon">
                          <Wrench size={16} />
                        </div>

                        <strong>{item.name}</strong>

                      </div>
                    </td>

                    <td>
                      <span className="additional-service-category">
                        {item.category}
                      </span>
                    </td>

                    <td>
                      <span className="additional-service-description">
                        {item.description}
                      </span>
                    </td>

                    <td>
                      <div className="additional-service-price">

                        <strong>
                          {formatPrice(item)}
                        </strong>

                        <span>
                          {item.pricingType}
                        </span>

                      </div>
                    </td>

                    <td>

                      <button
                        className={`additional-service-status ${
                          item.status === "Active"
                            ? "active"
                            : "inactive"
                        }`}
                        onClick={() =>
                          toggleStatus(item.id)
                        }
                      >
                        {item.status}
                      </button>

                    </td>

                    <td>

                      <div className="additional-service-actions">

                        <button
                          className="additional-service-action edit"
                          title="Edit"
                          onClick={() =>
                            handleEdit(item)
                          }
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          className="additional-service-action delete"
                          title="Delete"
                          onClick={() =>
                            deleteService(item.id)
                          }
                        >
                          <Trash2 size={16} />
                        </button>

                      </div>

                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="additional-service-empty"
                  >
                    No additional services found
                  </td>
                </tr>
              )}

            </tbody>

          </table>

        </div>

        {/* Pagination */}
        <div className="additional-service-pagination">

          <span>
            Showing{" "}
            {filteredServices.length === 0
              ? 0
              : (currentPage - 1) * rowsPerPage + 1}{" "}
            -{" "}
            {Math.min(
              currentPage * rowsPerPage,
              filteredServices.length
            )}{" "}
            of {filteredServices.length}
          </span>

          <div className="additional-service-pagination-buttons">

            <button
              disabled={currentPage === 1}
              onClick={() =>
                setPage((prev) =>
                  Math.max(1, prev - 1)
                )
              }
            >
              Previous
            </button>

            {Array.from(
              { length: totalPages },
              (_, index) => index + 1
            ).map((pageNumber) => (
              <button
                key={pageNumber}
                className={
                  currentPage === pageNumber
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setPage(pageNumber)
                }
              >
                {pageNumber}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() =>
                setPage((prev) =>
                  Math.min(
                    totalPages,
                    prev + 1
                  )
                )
              }
            >
              Next
            </button>

          </div>

        </div>

      </div>

      {/* Mobile */}
      <div className="additional-service-mobile-list">

        {currentRows.length > 0 ? (
          currentRows.map((item) => (
            <div
              className="additional-service-mobile-card"
              key={item.id}
            >

              <div className="additional-service-mobile-top">

                <div className="additional-service-name">

                  <div className="additional-service-row-icon">
                    <Wrench size={16} />
                  </div>

                  <div>
                    <strong>{item.name}</strong>

                    <span>
                      {item.category}
                    </span>
                  </div>

                </div>

                <button
                  className={`additional-service-status ${
                    item.status === "Active"
                      ? "active"
                      : "inactive"
                  }`}
                  onClick={() =>
                    toggleStatus(item.id)
                  }
                >
                  {item.status}
                </button>

              </div>

              <p className="additional-service-mobile-description">
                {item.description}
              </p>

              <div className="additional-service-mobile-price">

                <div>
                  <span>Price</span>
                  <strong>
                    {formatPrice(item)}
                  </strong>
                </div>

                <div>
                  <span>Pricing Type</span>
                  <strong>
                    {item.pricingType}
                  </strong>
                </div>

              </div>

              <div className="additional-service-mobile-actions">

                <button
                  className="mobile-additional-service-edit"
                  onClick={() =>
                    handleEdit(item)
                  }
                >
                  <Pencil size={15} />
                  Edit
                </button>

                <button
                  className="mobile-additional-service-delete"
                  onClick={() =>
                    deleteService(item.id)
                  }
                >
                  <Trash2 size={15} />
                  Delete
                </button>

              </div>

            </div>
          ))
        ) : (
          <div className="additional-service-mobile-empty">
            No additional services found
          </div>
        )}

      </div>

    </div>
  );
}